/* eslint-disable react/destructuring-assignment */
import { Entity } from "@axl/membership-sdk";
import { Table as ATable, TableProps as ATableProps } from "antd";
import { SizeType } from "antd/es/config-provider/SizeContext";
import {
  SorterResult,
  TableCurrentDataSource,
  ColumnType,
  Key,
  TablePaginationConfig,
} from "antd/es/table/interface";
import cn from "classnames";
import { useCallback, useEffect, useMemo, useState } from "react";

import { SortingDirection } from "@app/shared/lib/enums/sorting-direction";
import { useLoc } from "@app/shared/lib/hooks";

import styles from "./Table.module.scss";

type Filters = {
  pageTotal: number;
  itemsTotal: number;
  take: number;
  page: number;
  sortName?: string;
  sortType: SortingDirection;
};

export type OmittedTableProps<T extends Entity> = Omit<
  ATableProps<T>,
  "columns" | "dataSource" | "loading" | "size"
>;
type TableProps<T extends Entity> = OmittedTableProps<T> & {
  columns: ColumnType<T>[];
  fetchItemsByFilter: (selectedFilters: {
    pageTotal: number;
    itemsTotal: number;
    take: number;
    page: number;
    sortName?: string;
    sortType: SortingDirection;
  }) => Promise<{ filters: Filters; items: T[] }>;
  defaultFilters: { take: number };
  dataSource?: T[];
  filter?: React.ReactNode;
  filterSticky?: boolean;
  disablePagination?: boolean;
  showSizeChanger?: boolean;
  autoFetch?: boolean;
  hidePaginationOnePage?: boolean;
  size?: SizeType;
  rowBordered?: boolean;
  loading?: boolean;
};

export function Table<T extends Entity>(props: TableProps<T>) {
  const [selectedFilters, setSelectedFilters] = useState<Filters>({
    ...props.defaultFilters,
    itemsTotal: 0,
    page: 0,
    pageTotal: 0,
    sortType: SortingDirection.asc,
  });
  const [isFetching, setFetching] = useState(true);
  const [items, setItems] = useState<T[]>([]);
  const loc = useLoc();

  const disablePagination = useMemo(() => {
    const filter = selectedFilters;

    return (
      props.disablePagination === true ||
      (props.hidePaginationOnePage !== false && filter.pageTotal === 1)
    );
  }, [props.disablePagination, props.hidePaginationOnePage, selectedFilters]);

  const pagination = useMemo<TablePaginationConfig | false>(() => {
    if (disablePagination) return false;
    const filter = selectedFilters;

    return {
      showSizeChanger: props.showSizeChanger ?? true,
      showTotal: (total, range) => {
        return `${range[0]}-${range[1]} ${loc.word("Global.of")} ${total}`;
      },
      total: filter.itemsTotal,
      pageSize: filter.take,
      current: filter.page,
    };
  }, [disablePagination, loc, selectedFilters, props.showSizeChanger]);

  const fetch = useCallback(async () => {
    setFetching(true);
    try {
      const { filters, items: fetchedItems } =
        await props.fetchItemsByFilter(selectedFilters);
      setSelectedFilters(filters);
      setItems(fetchedItems);
    } finally {
      setFetching(false);
    }
  }, [props, selectedFilters]);

  useEffect(() => {
    fetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- refetch on update function
  }, [props.fetchItemsByFilter]);

  const handleTableChange = useCallback(
    (
      tablePagination: TablePaginationConfig,
      filters: Record<string, (Key | boolean)[] | null>,
      sorter: SorterResult<T> | SorterResult<T>[],
      extra: TableCurrentDataSource<T>,
      // eslint-disable-next-line max-params
    ) => {
      // page with this.props.store.updateFilter
      switch (extra.action) {
        case "paginate": {
          const take = tablePagination.pageSize ?? selectedFilters.take;
          const page = tablePagination.current ?? selectedFilters.page;
          setSelectedFilters({ ...selectedFilters, take, page });
          break;
        }

        case "sort":
          setSelectedFilters({
            ...selectedFilters,
            // dirty hack
            sortName: (sorter as SorterResult<T>).field as string,
            sortType:
              (sorter as SorterResult<T>).order === "ascend"
                ? SortingDirection.asc
                : SortingDirection.desc,
          });
          break;
        default:
          break;
      }

      setSelectedFilters({
        ...selectedFilters,
        ...filters,
      });
      props.onChange?.(tablePagination, filters, sorter, extra);
      fetch();
    },
    [props, selectedFilters, fetch],
  );

  return (
    <>
      {props.filter && (
        <div
          className={cn(styles.filter, "p-7 bg-white")}
          data-sticky={props.filterSticky ?? true}
        >
          {props.filter}
        </div>
      )}
      <ATable<T>
        {...props}
        className={cn(
          props.className,
          props.rowBordered === false ? styles.noRowBorder : undefined,
        )}
        size={props.size}
        rowKey={props.rowKey ? props.rowKey : (x) => x.id}
        columns={props.columns}
        scroll={{ scrollToFirstRowOnChange: true, ...props.scroll }}
        dataSource={props.dataSource ?? items}
        loading={props.loading ?? isFetching}
        pagination={pagination}
        // eslint-disable-next-line max-params
        onChange={(p, f, s, e) => handleTableChange(p, f, s, e)}
      />
    </>
  );
}
